import { USER_PAGE_TOAST } from "common/Constants/UserPageToast.constants";
import { lazy, Suspense, useContext, useEffect, useRef, useState } from "react";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import AddIcon from "@mui/icons-material/Add";
import PageTitle from "common/Components/PageTitle/PageTitle";
import { UsersList } from "./Components";
import CustomerInviteDrawer from "./Components/CustomerInviteDrawer";
import { useUsersViewInitialization } from "./Users.hook";
import UserContext from "common/Context/UserContext";
import { useNavigate } from "react-router-dom";
import { DEFAULT_CUSTOMER_USERS_LIST_FILTERS, USERS_PAGE_CONSTANTS } from "./Users.constants";
import { PageInviteButton, UsersPageContainer } from "./Users.styles";

const PageNotFound = lazy(() => import("common/Components/PageNotFound"));
const ErrorPage = lazy(() => import("common/Components/ErrorPage"));
const Toast = lazy(() => import("common/Components/Toast"));

const Users = () => {
  const { state, actions, toast, dismissToast } = useUsersViewInitialization();
  const { customerRoleOptions, customerRoleLabelMap } = useContext(UserContext);
  const { getCustomerCode, getSelectedCustomerId, getCustomerName } =
    useContext(UserContext);
  const navigate = useNavigate();
  const getCustomerCodeRef = useRef(getCustomerCode);
  const getSelectedCustomerIdRef = useRef(getSelectedCustomerId);
  const actionsRef = useRef(actions);
  const lastFetchKeyRef = useRef(null);
  const [inviteDrawerOpen, setInviteDrawerOpen] = useState(false);
  getCustomerCodeRef.current = getCustomerCode;
  getSelectedCustomerIdRef.current = getSelectedCustomerId;
  actionsRef.current = actions;

  useEffect(() => {
    const fetchUsersIfContext = () => {
      const customerCode = getCustomerCodeRef.current();
      const customerId = getSelectedCustomerIdRef.current?.();
      if (
        !customerCode ||
        customerId == null ||
        String(customerId).trim() === ""
      ) {
        return;
      }
      const key = `${customerId}:${customerCode}`;
      if (lastFetchKeyRef.current === key) return;
      lastFetchKeyRef.current = key;
      actionsRef.current.getAllUsers(customerId, customerCode, {});
    };
    fetchUsersIfContext();
    window.addEventListener("customerChanged", fetchUsersIfContext);
    return () =>
      window.removeEventListener("customerChanged", fetchUsersIfContext);
  }, []);

  const refreshUserList = () => {
    actions.getAllUsers(getSelectedCustomerId(), getCustomerCode(), {
      page: state.pagination.page,
      size: state.pagination.size,
      filters: state.listFilters,
    });
  };

  if (state.error) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <ErrorPage
          errorValue={state.error}
          handleGoBack={() => {
            actions.removeError();
            navigate("/");
          }}
          buttonTitle="Go Back"
        />
      </Suspense>
    );
  }
  if (!getCustomerCode()) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <PageNotFound
          statusCode="3"
          errorText={USERS_PAGE_CONSTANTS.SELECT_CUSTOMER_REQUIRED_ERROR}
        />
      </Suspense>
    );
  }
  if (
    getSelectedCustomerId() == null ||
    String(getSelectedCustomerId()).trim() === ""
  ) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <PageNotFound
          statusCode="3"
          errorText={USERS_PAGE_CONSTANTS.MISSING_CUSTOMER_ID_ERROR}
        />
      </Suspense>
    );
  }

  return (
    <UsersPageContainer>
      <Suspense fallback={null}>
        <Toast
          type={toast.type}
          title={toast.title}
          description={toast.description}
          show={toast.show}
          onClose={dismissToast}
          duration={USER_PAGE_TOAST.DURATION}
        />
      </Suspense>
      <PageTitle
        title={USERS_PAGE_CONSTANTS.PAGE_TITLE}
        subTitle={USERS_PAGE_CONSTANTS.PAGE_SUBTITLE}
        icon={<PeopleOutlinedIcon />}
        action={
          <PageInviteButton
            type="button"
            onClick={() => setInviteDrawerOpen(true)}
            data-testid="invite-btn"
            startIcon={<AddIcon />}
          >
            {USERS_PAGE_CONSTANTS.INVITE_USER_LABEL}
          </PageInviteButton>
        }
      />
      <UsersList
        customerId={getSelectedCustomerId()}
        users={state.usersList}
        loading={state.loading}
        pagination={state.pagination}
        listFilters={state.listFilters}
        onClearFilters={() => {
          actions.onListFiltersChange(
            getSelectedCustomerId(),
            getCustomerCode(),
            { ...DEFAULT_CUSTOMER_USERS_LIST_FILTERS },
            state.pagination.size
          );
        }}
        onListFiltersChange={(rawFilters) => {
          actions.onListFiltersChange(
            getSelectedCustomerId(),
            getCustomerCode(),
            rawFilters,
            state.pagination.size
          );
        }}
        roleOptions={customerRoleOptions}
        roleLabelMap={customerRoleLabelMap}
        onAssignRoles={async (user, roles) => {
          const result = await actions.assignCustomerUserRoles(
            getSelectedCustomerId(),
            getCustomerCode(),
            user,
            roles
          );
          if (result.ok) {
            refreshUserList();
          }
          return result;
        }}
        onPatchCustomerAccess={async (user, partial) => {
          const result = await actions.patchCustomerUserAccess(
            getSelectedCustomerId(),
            getCustomerCode(),
            {
              accessId: user.accessId,
              userId: user.userId ?? user.id,
              ...partial,
            }
          );
          if (result.ok) {
            refreshUserList();
          }
          return result;
        }}
        onRowAction={async (user, action, roles) => {
          const result = await actions.onCustomerUserListAction(
            getSelectedCustomerId(),
            getCustomerCode(),
            user,
            action,
            roles
          );
          if (result?.ok) {
            refreshUserList();
          }
          return result;
        }}
        fetchUsers={(page, nextSize) => {
          actions.getAllUsers(getSelectedCustomerId(), getCustomerCode(), {
            page: page ?? 0,
            size: nextSize ?? state.pagination.size,
            filters: state.listFilters,
          });
        }}
      />
      <CustomerInviteDrawer
        open={inviteDrawerOpen}
        onClose={() => setInviteDrawerOpen(false)}
        roleOptions={customerRoleOptions}
        tenantName={
          typeof getCustomerName === "function" ? getCustomerName() : ""
        }
        checkInviteEmail={(email) =>
          actions.checkInviteEmailExists(email, getSelectedCustomerId())
        }
        onSubmit={async (payload) => {
          const ok = await actions.inviteCustomerUser(
            getSelectedCustomerId(),
            getCustomerCode(),
            payload
          );
          if (ok) {
            setInviteDrawerOpen(false);
            refreshUserList();
          }
          return ok;
        }}
      />
    </UsersPageContainer>
  );
};

export default Users;
